/**
 * Mutations for auth
 */
export const mutations = {
  /*
   * SIMPLE SETTERS
   */
  setUsername(state, payload = "") {
    state.username = payload;
  },
  setShowAddToHomePopup(state, value) {
    state.showAddToHomePopup = value;
  },
  setAddToHomeEvent(state, value) {
    state.addToHomeEvent = value;
  },
  setShowNotificationPopup(state, value) {
    state.showNotificationPopup = value;
  },
  setUserId(state, payload = 0) {
    state.userId = payload;
  },
  setToken(state, payload = "") {
    state.token = payload;
  },
  setMessage(state, payload = "") {
    state.message = payload;
  },
  setBottomMessage(state, payload = "") {
    state.bottomMessage = payload;
  },
  setPermittedUsers(state, users) {
    state.permittedUserList = users;
  },
  setSocketAuthorized(state, value = false) {
    state.socketAuthorized = value === true;
  },
  beforeRouteAnimating(state) {
    state.routeAnimating = true;
  },
  afterRouteAnimating(state) {
    state.routeAnimating = false;
  },
  setSelectedProviderID(state, json) {
    state.selectedProviderId =
      json.userRole === 24 ? parseInt(json["subscriber_id"]) : state.userId;
  },
  setNextRoute(state, value) {
    state.nextRoute = value;
  },
  setLoadingAuth(state, value) {
    state.loadingAuth = value;
  },
  setUserRole(state, role) {
    state.userRole = isNaN(role) ? 0 : parseInt(role);
  },
  setUsetType(state, type) {
    state.userType = isNaN(type) ? -1 : parseInt(type);
  },
  setLoggedInTimezone(state, value) {
    state.loggedInTimezone = value;
  },

  /**
   * COMPLEX SETTERS
   */

  /**
   * Set if user has the permission to add and delete appointments
   *
   * If both permission id and permission status are null, the user has permission to edit and delete
   *
   * @param {Object} state
   * @param {*} id - Permission ID
   * @param {*} status - Permission status
   */
  setAddionalPermissionToAddEditAndDelete(state, { id, status }) {
    if (id === null && status === null) {
      state.addionalPermissionToAddEditAndDelete = true;
    }
  },

  /**
   * Setting permission details about weather an user has the permission to add and delete video appointment
   * For non app users
   *
   * For non app users (subscriber) if videoUserID is set,
   * it means that the user has permission to send video appointments
   *
   * @param {Object} state
   * @param {*} videoUserID
   */
  setSecureVideoPermissionNonApp(state, { videoUserID, connectUserID }) {
    if (videoUserID !== null || connectUserID !== null) {
      state.add_video_appointment_permission = true;
      state.delete_video_appointment_permission_refused = false;
    } else {
      state.add_video_appointment_permission = false;
      state.delete_video_appointment_permission_refused = true;
    }
  },

  /**
   * Setting permission details about weather an user has the permission to add and delete video appointment
   * For app users
   *
   * @param {Object} state
   * @param {*} add_video_appointment_permission
   * @param {*} delete_video_appointment_permission_refused
   */
  setSecureVideoPermissionApp(
    state,
    {
      videoUserID,
      connectUserID,
      add_video_appointment_permission,
      delete_video_appointment_permission_refused
    }
  ) {
    if (videoUserID !== null || connectUserID !== null) {
      state.add_video_appointment_permission =
        add_video_appointment_permission !== null;
      state.delete_video_appointment_permission_refused =
        delete_video_appointment_permission_refused !== null;
    } else {
      state.add_video_appointment_permission = false;
      state.delete_video_appointment_permission_refused = true;
    }
  },

  /**
   * Change the selected provider id and update corresponding details
   *
   * @param {Object} state
   * @param {Id} id - Set the selected provider id
   */
  changeSelectedProviderID(state, id) {
    // Step 1: Check if id is int
    if (isNaN(id)) {
      return false;
    }

    // Step 2: Get the index of the selected user
    const index = state.permittedUserList
      .map(user => {
        return user.id;
      })
      .indexOf(id);

    // Step 3: If index is not found stop
    if (index === -1) {
      return false;
    }

    // Step 4: Set current provider id
    id = parseInt(id);
    state.selectedProviderId = id;

    // Step 5: Set permission of that user
    state.addionalPermissionToAddEditAndDelete =
      state.permittedUserList[index]["permission_id"] === null &&
      state.permittedUserList[index]["permission_status"] === null;

    // Step 6: Setting video permission for that user
    if (state.permittedUserList[index]["videoUserID"] !== null || state.permittedUserList[index]["connectUserID"] !== null) {
      state.add_video_appointment_permission =
        state.permittedUserList[index]["add_video_appointment_permission"] !==
        null;
      state.delete_video_appointment_permission_refused =
        state.permittedUserList[index][
          "delete_video_appointment_permission_refused"
        ] !== null;
    } else {
      state.add_video_appointment_permission = false;
      state.delete_video_appointment_permission_refused = true;
    }

    // Step 7: Set the names
    state.firstName = state.permittedUserList[index].firstName;
    state.lastName = state.permittedUserList[index].lastName;
    state.middleName = state.permittedUserList[index].middleName;
    state.namePrefix = state.permittedUserList[index].namePrefix;
    state.nameSuffix = state.permittedUserList[index].nameSuffix;
    state.credentials = state.permittedUserList[index].credentials;
    state.videoUserId = state.permittedUserList[index]['videoUserID'];
    state.connectUserId = state.permittedUserList[index]['connectUserID'];
  },

  /**
   * Getting user session data from local storage and keeping it in the state
   *
   * @param {Object} state
   */
  setLocal(state) {
    if (state.fromLocal) {
      return;
    }
    // Step 1: Get data from local storage
    let user = window.sessionStorage.getItem("auth");
    try {
      // Step 1.1: parse the JSON
      user = JSON.parse(user);

      // Step 1.2: Check if token is formatted properly
      if (!("token" in user) || !("userId" in user)) {
        return window.sessionStorage.removeItem("auth");
      }

      // Step 1.3: When properly formatted set the token and userId in state
      state.token = user.token;
      state.userId = parseInt(user.userId);
    } catch (e) {
      // Step 1.1: Incase of any error remove the auth token
      window.sessionStorage.removeItem("auth");
    }

    // Step 2: Set the from local as true, meaning that token has been loaded from local storage
    state.fromLocal = true;
  },

  /**
   * Bringing state back to default
   *
   * @param {Object} state
   */
  removeAuth(state) {
    window.sessionStorage.removeItem("auth");
    state.loadingAuth = false;
    state.username = "";
    state.userId = 0;
    state.token = "";
    state.message = "";
    state.bottomMessage = "";
    state.userRole = 0;
    state.userType = -1;
    state.fromLocal = false;
    state.firstName = "";
    state.lastName = "";
    state.middleName = "";
    state.namePrefix = "";
    state.nameSuffix = "";
    state.credentials = "";
    state.permittedUserList = [];
    state.selectedProviderId = 0;
    state.addionalPermissionToAddEditAndDelete = false;
    state.add_video_appointment_permission = false;
    state.delete_video_appointment_permission_refused = true;
    state.socketAuthorized = false;
  },
  setUserDetails(state, json) {
    if ("firstName" in json) {
      state.firstName = json.firstName.trim();
    }
    if ("lastName" in json) {
      state.lastName = json.lastName.trim();
    }
    if ("middleName" in json) {
      state.middleName = json.middleName.trim();
    }
    if ("namePrefix" in json) {
      state.namePrefix = json.namePrefix.trim();
    }
    if ("nameSuffix" in json) {
      state.nameSuffix = json.nameSuffix.trim();
    }
    if ("credentials" in json) {
      state.credentials = json.credentials.trim();
    }
    if ("videoUserID" in json) {
      state.videoUserId = json.videoUserID;
    }
    if ("connectUserID" in json) {
      state.connectUserId = json.connectUserID;
    }
  },
  setUserSubscription(state, subscription) {
    state.userSubscription = subscription;
  },
  setEmailTabPermission(state, permission) {
    state.emailTabPermission[state.selectedProviderId] = permission;
  }
};
